/**
 * Builds the promote-external payload that converts a Google/Outlook calendar
 * event into a managed Scrambl Event. The server call is idempotent — an
 * already-promoted calendar item resolves to its existing Scrambl event.
 */
import type {
  CalendarExternalEvent,
  PromoteExternalCalendarEventInput,
} from '@scrambl/shared'

function mapExternalProvider(event: CalendarExternalEvent) {
  if (event.provider) return event.provider
  return event.type === 'google_calendar'
    ? 'GOOGLE_CALENDAR'
    : 'OUTLOOK_CALENDAR'
}

function toIsoString(value?: Date | string | null): string | null {
  if (!value) return null
  if (value instanceof Date) return value.toISOString()

  const parsedDate = new Date(value)
  return Number.isNaN(parsedDate.getTime()) ? null : parsedDate.toISOString()
}

export function buildPromotionPayload(
  event: CalendarExternalEvent,
): PromoteExternalCalendarEventInput {
  const updatedAt = toIsoString(event.updatedAt)

  return {
    externalEvent: {
      provider: mapExternalProvider(event),
      calendarId: event.calendarId,
      externalEventId: event.externalId,
      iCalUid: event.iCalUid ?? null,
      recurringEventId: event.recurringEventId ?? null,
      originalStartTime: event.originalStartTime ?? null,
      updatedAt,
      etag: event.etag ?? null,
      htmlLink: event.htmlLink ?? null,
      organizerEmail: event.organizerEmail ?? null,
      title: event.title,
      description: event.description ?? null,
      location: event.location ?? null,
      status: event.status ?? null,
      isBusy: event.isBusy,
      isAllDay: event.allDay,
      start: event.start.toISOString(),
      end: event.end ? event.end.toISOString() : null,
      attendees: (event.attendees ?? [])
        .filter(attendee => Boolean(attendee.email))
        .map(attendee => ({
          email: attendee.email,
          name: attendee.name ?? null,
          responseStatus: attendee.responseStatus ?? null,
          organizer:
            event.organizerEmail?.toLowerCase() ===
            attendee.email.toLowerCase(),
        })),
      rawPayload: {
        ...event,
        start: event.start.toISOString(),
        end: event.end ? event.end.toISOString() : null,
        updatedAt,
      },
    },
  }
}
